import { useState, useEffect, useCallback } from 'react';
import MyApiClient from '../api-client/MyApiClient';

// Cache globale per le memberships
const membershipsCache = {
  data: null,
  timestamp: null,
  isLoading: false
};

// Listeners per notificare i componenti quando i dati cambiano
const listeners = new Set();

const notifyListeners = (data) => {
  listeners.forEach(listener => listener(data));
};

// Durata del cache: 2 minuti (le richieste possono cambiare più frequentemente del profilo)
const CACHE_DURATION = 2 * 60 * 1000;

export const useMemberships = ({
  autoFetch = false,
  membershipRanks = null,
  notMembershipRanks = null,
  statusFilter = null
} = {}) => {
  const [memberships, setMemberships] = useState(membershipsCache.data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Funzione per ottenere il token
  const getToken = useCallback(() => {
    const tokenDetailsString = sessionStorage.getItem('userDetails');
    if (tokenDetailsString) {
      const tokenDetails = JSON.parse(tokenDetailsString);
      return tokenDetails.idToken;
    }
    return null;
  }, []);

  // Verifica se il cache è ancora valido
  const isCacheValid = useCallback(() => {
    if (!membershipsCache.data || !membershipsCache.timestamp) {
      return false;
    }
    return Date.now() - membershipsCache.timestamp < CACHE_DURATION;
  }, []);

  // Genera una chiave per il cache basata sui parametri
  const getCacheKey = useCallback(() => {
    return `${membershipRanks || 'null'}-${notMembershipRanks || 'null'}-${statusFilter || 'null'}`;
  }, [membershipRanks, notMembershipRanks, statusFilter]);

  // Funzione per caricare le memberships
  const fetchMemberships = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('Token non disponibile');
      return null;
    }

    // Se il cache è valido e i parametri sono gli stessi, restituisci i dati cached
    if (isCacheValid() && membershipsCache.cacheKey === getCacheKey()) {
      setMemberships(membershipsCache.data);
      return membershipsCache.data;
    }

    // Se c'è già una richiesta in corso, aspetta che finisca
    if (membershipsCache.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!membershipsCache.isLoading) {
            resolve(membershipsCache.data);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    try {
      membershipsCache.isLoading = true;
      setLoading(true);
      setError(null);

      const apiClient = new MyApiClient();
      const risposta = await apiClient.getMemberships(
        token,
        membershipRanks,
        notMembershipRanks,
        statusFilter
      );
      // Un account cancellato o una risposta d'errore non e` un elenco: senza questo il menu laterale va in errore
      const membershipData = Array.isArray(risposta) ? risposta : [];

      // Aggiorna il cache
      membershipsCache.data = membershipData;
      membershipsCache.timestamp = Date.now();
      membershipsCache.cacheKey = getCacheKey();
      
      setMemberships(membershipData);
      notifyListeners(membershipData);
      
      return membershipData;
    } catch (err) {
      console.error('Errore nel caricamento delle memberships:', err);
      setError(err.message || 'Errore nel caricamento delle memberships');
      return null;
    } finally {
      membershipsCache.isLoading = false;
      setLoading(false);
    }
  }, [getToken, isCacheValid, getCacheKey, membershipRanks, notMembershipRanks, statusFilter]);

  // Funzione per invalidare il cache
  const invalidateCache = useCallback(() => {
    membershipsCache.data = null;
    membershipsCache.timestamp = null;
    membershipsCache.cacheKey = null;
  }, []);

  // Funzione per aggiornare le memberships
  const updateMemberships = useCallback((updatedMemberships) => {
    membershipsCache.data = updatedMemberships;
    membershipsCache.timestamp = Date.now();
    membershipsCache.cacheKey = getCacheKey();
    setMemberships(updatedMemberships);
    notifyListeners(updatedMemberships);
  }, [getCacheKey]);

  // Effetto per il fetch automatico
  useEffect(() => {
    if (autoFetch) {
      fetchMemberships();
    }
  }, [autoFetch, fetchMemberships]);

  // Effetto per ascoltare i cambiamenti del cache
  useEffect(() => {
    const listener = (data) => {
      setMemberships(data);
    };
    
    listeners.add(listener);
    
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Funzioni di utilità per filtrare i dati
  const getPendingRequests = useCallback(() => {
    if (!memberships) return [];
    return memberships.filter(member => member.status !== 'CONFIRMED');
  }, [memberships]);

  const getConfirmedMembers = useCallback((role = null, type = null) => {
    if (!memberships) return [];
    
    return memberships.filter(member => {
      const isConfirmed = member.status === 'CONFIRMED';
      const matchesRole = !role || member.membershipRank === role;
      const matchesType = !type || (member.membershipRank === 'MEMBER' && member.member && member.member.type === type);
      return isConfirmed && matchesRole && matchesType;
    });
  }, [memberships]);

  // Il badge e` un avviso, quindi conta solo le richieste ancora da evadere.
  // Le rifiutate restano in elenco per sempre: conteggiarle terrebbe la pallina
  // accesa su qualcosa che non richiede piu` nessuna azione.
  const getBadgeCount = useCallback(() => {
    if (!memberships) return 0;
    return memberships.filter(member => member.status === 'PENDING').length;
  }, [memberships]);

  return {
    memberships,
    loading,
    error,
    fetchMemberships,
    invalidateCache,
    updateMemberships,
    // Funzioni di utilità
    getPendingRequests,
    getConfirmedMembers,
    getBadgeCount
  };
};

export default useMemberships;